import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowLeft, Leaf, Sparkles, UserRound, CalendarDays, Utensils } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { DemoRecipeCard } from "@/components/demo/DemoRecipeCard";
import { DemoRecipeDialog } from "@/components/demo/DemoRecipeDialog";
import { DemoSlotPicker } from "@/components/demo/DemoSlotPicker";
import { DemoMenu } from "@/components/demo/DemoMenu";
import { DemoGrocery } from "@/components/demo/DemoGrocery";
import { DemoPlans } from "@/components/demo/DemoPlans";
import { toast } from "sonner";
import { DAYS, SLOTS_BY_MEALS, isCompatible, buildGrocery, loadDemoPlan, saveDemoPlan, loadDemoSettings, saveDemoSettings, prunePlan } from "@/lib/demo";

const SAVED = loadDemoSettings();

const GOALS = ["demo_goal_weight", "demo_goal_balance", "demo_goal_muscle", "demo_goal_energy", "demo_goal_budget", "demo_goal_time"];
const ALLERGENS = ["gluten", "dairy", "nuts", "eggs", "fish", "shellfish", "soy"];
const DIETS = ["", "flexitarian", "vegetarian", "vegan", "pescatarian"];
const CATS = ["breakfast", "lunch", "dinner", "snack"];
const BILLING_COUNTRIES = ["CH", "FR", "BE", "LU", "DE", "IT"];
const initialBillingCountry = () => {
  const region = typeof navigator !== "undefined" ? navigator.language.split("-").pop()?.toUpperCase() : "";
  return BILLING_COUNTRIES.includes(region) ? region : "FR";
};

function Choice({ active, onClick, children, testId }) {
  return (
    <button type="button" data-testid={testId} aria-pressed={active} onClick={onClick}
      className={"rounded-full border px-3.5 py-2.5 text-sm font-medium transition-colors " + (active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-white/70 text-foreground hover:border-primary/60")}>
      {children}
    </button>
  );
}

export default function Demo() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [billingCountry, setBillingCountry] = useState(initialBillingCountry);
  const { data: pricing, isLoading: pricingLoading, isError: pricingError } = useQuery({
    queryKey: ["demo-plans", billingCountry],
    queryFn: async () => (await api.get("/public/plans", { params: billingCountry ? { country: billingCountry } : {} })).data,
  });
  const { data: recipePayload } = useQuery({
    queryKey: ["demo-recipes", billingCountry],
    queryFn: async () => (await api.get("/public/recipes", { params: billingCountry ? { country: billingCountry } : {} })).data,
  });
  const [tab, setTab] = useState("customize");
  const [allergies, setAllergies] = useState(SAVED.allergies || []);
  const [diet, setDiet] = useState(SAVED.diet || "");
  const [age, setAge] = useState(SAVED.age || "");
  const [sex, setSex] = useState(SAVED.sex || "");
  const [goals, setGoals] = useState(SAVED.goals?.length ? SAVED.goals : ["demo_goal_balance"]);
  const [persons, setPersons] = useState(SAVED.persons || 1);
  const [answered, setAnswered] = useState(SAVED.answered || []);
  const answer = (k) => setAnswered((c) => (c.includes(k) ? c : [...c, k]));
  const [days, setDays] = useState(SAVED.days || DAYS);
  const [mealsPerDay, setMealsPerDay] = useState(SLOTS_BY_MEALS[SAVED.mealsPerDay] ? SAVED.mealsPerDay : 3);
  const [cat, setCat] = useState("");
  const [plan, setPlan] = useState(loadDemoPlan);
  const [viewing, setViewing] = useState(null);
  const [picking, setPicking] = useState(null);
  useEffect(() => saveDemoPlan(plan), [plan]);
  useEffect(() => { if (answered.length) saveDemoSettings({ allergies, diet, days, mealsPerDay, age, sex, goals, persons, answered }); }, [allergies, diet, days, mealsPerDay, age, sex, goals, persons, answered]);

  const recipes = useMemo(() => recipePayload?.recipes ?? [], [recipePayload]);
  const recipesById = useMemo(() => Object.fromEntries(recipes.map((r) => [r.id, r])), [recipes]);
  const compatible = useMemo(() => recipes.filter((r) => isCompatible(r, allergies, diet)), [recipes, allergies, diet]);
  const compatibleById = useMemo(() => Object.fromEntries(compatible.filter((r) => r.tier === "free").map((r) => [r.id, r])), [compatible]);
  const slots = SLOTS_BY_MEALS[mealsPerDay];
  useEffect(() => {
    if (!recipes.length) return;
    const [next, removed] = prunePlan(plan, compatibleById);
    if (removed) { setPlan(next); toast(t("dm_pruned", { count: removed })); }
  }, [compatibleById, recipes.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const grocery = useMemo(() => buildGrocery(plan, compatibleById, days, slots, persons), [plan, compatibleById, days, slots, persons]);
  const shownRecipes = compatible.filter((r) => !cat || r.category === cat);

  const toggleGoal = (v) => { answer("goals"); setGoals((c) => c.includes(v) ? (c.length > 1 ? c.filter((x) => x !== v) : c) : [...c, v]); };
  const toggleAllergy = (v) => { answer("allergens"); setAllergies((c) => c.includes(v) ? c.filter((x) => x !== v) : [...c, v]); };
  const toggleDay = (d) => { answer("days"); setDays((c) => c.includes(d) ? (c.length > 1 ? c.filter((x) => x !== d) : c) : [...c, d]); };
  const pick = ({ day, slot }, recipeId) => setPlan((p) => ({ ...p, [day]: { ...(p[day] || {}), [slot]: recipeId } }));
  const remove = (day, slot) => setPlan((p) => { const d = { ...(p[day] || {}) }; delete d[slot]; return { ...p, [day]: d }; });

  const tabs = [["customize", "dm_tab_profile"], ["recipes", "dm_tab_recipes"], ["plan", "dm_tab_menu"], ["grocery", "dm_tab_grocery"]];

  return (
    <main className="min-h-screen chenia-hero-gradient">
      <header className="mx-auto max-w-6xl px-4 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-foreground">
          <span className="grid place-items-center h-10 w-10 rounded-2xl bg-primary text-primary-foreground"><Leaf className="h-5 w-5" /></span>
          <span className="font-serif text-2xl font-semibold tracking-tight">CHENIA</span>
        </Link>
        <div className="flex items-center gap-2"><LanguageSelector /><Link to="/login"><Button variant="ghost" className="rounded-full">{t("login")}</Button></Link></div>
      </header>

      <section className="mx-auto max-w-5xl px-4 sm:px-8 pb-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />{t("demo_return")}</Link>
        <p className="mt-7 inline-flex items-center gap-2 rounded-full bg-chenia-rosesoft px-3 py-1 text-xs font-semibold uppercase tracking-widest text-chenia-terracottadark"><Sparkles className="h-3.5 w-3.5" />{t("demo_sample")}</p>
        <h1 className="mt-3 max-w-3xl font-serif text-4xl font-semibold tracking-tight sm:text-5xl">{t("demo_title")}</h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">{t("demo_subtitle")}</p>

        <div className="mt-8 grid grid-cols-2 gap-2 rounded-3xl border border-border bg-white/70 p-1 sm:grid-cols-4" role="tablist">
          {tabs.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`demo-tab-${id}`}
              className={"rounded-2xl px-3 py-3 text-sm font-semibold transition-colors " + (tab === id ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}>
              {t(label)}
            </button>
          ))}
        </div>

        {tab === "customize" && (
          <section className="mt-5 chenia-card p-5 sm:p-8" aria-label={t("dm_tab_profile")}>
            <div className="flex items-start gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-chenia-sage/30 text-chenia-espresso"><UserRound className="h-5 w-5" /></span>
              <div><h2 className="font-serif text-2xl font-semibold">{t("demo_customize_title")}</h2><p className="mt-1 text-sm leading-relaxed text-muted-foreground">{t("demo_customize_subtitle")}</p></div>
            </div>
            <div className="mt-6 space-y-6">
              <div className="rounded-2xl border border-chenia-sage/40 bg-chenia-sage/15 p-4">
                <h3 className="text-sm font-semibold">{t("demo_sample_profiles_title")}</h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t("dm_profile_hint_optional")}</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="text-sm font-medium">{t("age")}
                    <input type="number" min="1" max="120" inputMode="numeric" value={age} onChange={(e) => { answer("age"); setAge(e.target.value); }} placeholder={t("demo_select_option")} className="mt-1 h-11 w-full rounded-xl border border-border bg-white px-3" data-testid="demo-age" />
                  </label>
                  <label className="text-sm font-medium">{t("sex")}
                    <select value={sex} onChange={(e) => { answer("sex"); setSex(e.target.value); }} className="mt-1 h-11 w-full rounded-xl border border-border bg-white px-3" data-testid="demo-sex">
                      <option value="">{t("demo_select_option")}</option><option value="male">{t("male")}</option><option value="female">{t("female")}</option><option value="other">{t("other")}</option>
                    </select>
                  </label>
                </div>
                <fieldset className="mt-4 space-y-2">
                  <legend className="text-sm font-semibold">{t("demo_goals_label")}</legend>
                  <div className="flex flex-wrap gap-2">{GOALS.map((g) => <Choice key={g} testId={`demo-goal-${g}`} active={goals.includes(g)} onClick={() => toggleGoal(g)}>{t(g)}</Choice>)}</div>
                </fieldset>
              </div>
              <fieldset className="space-y-3">
                <legend className="text-sm font-semibold">{t("demo_allergen_heading")}</legend>
                <p className="text-xs leading-relaxed text-muted-foreground">{t("demo_allergen_hint")}</p>
                <div className="flex flex-wrap gap-2">{ALLERGENS.map((a) => <Choice key={a} testId={`demo-allergen-${a}`} active={allergies.includes(a)} onClick={() => toggleAllergy(a)}>{t("al_" + a)}</Choice>)}</div>
              </fieldset>
              <fieldset className="space-y-2">
                <legend className="text-sm font-semibold">{t("dm_diet_label")}</legend>
                <select value={diet} onChange={(e) => { answer("diet"); setDiet(e.target.value); }} className="h-11 w-full rounded-xl border border-border bg-white px-3 sm:max-w-sm" data-testid="demo-diet">
                  {DIETS.map((d) => <option key={d} value={d}>{t(d ? `dm_diet_${d}` : "dm_diet_none")}</option>)}
                </select>
                <p className="text-xs leading-relaxed text-muted-foreground">{t("dm_filter_hint")}</p>
              </fieldset>
              <fieldset>
                <legend className="mb-2 inline-flex items-center gap-2 text-sm font-semibold"><CalendarDays className="h-4 w-4" />{t("demo_days_title")}</legend>
                <p className="mb-3 text-xs text-muted-foreground">{t("demo_days_hint")}</p>
                <div className="flex flex-wrap gap-2">{DAYS.map((d) => <Choice key={d} testId={"demo-day-" + d} active={days.includes(d)} onClick={() => toggleDay(d)}>{t(`demo_day_${d}`)}{!days.includes(d) ? " · " + t("demo_day_joker") : ""}</Choice>)}</div>
              </fieldset>
              <fieldset>
                <legend className="mb-2 inline-flex items-center gap-2 text-sm font-semibold"><Utensils className="h-4 w-4" />{t("demo_meals_title")}</legend>
                <div className="flex flex-wrap gap-2">{[3, 4, 5].map((n) => <Choice key={n} testId={"demo-meals-" + n} active={mealsPerDay === n} onClick={() => { answer("meals"); setMealsPerDay(n); }}>{n} {t("demo_meals_per_day")}</Choice>)}</div>
                <p className="mt-2 text-xs text-muted-foreground">{t("dm_meals_hint")}</p>
              </fieldset>
              <fieldset>
                <legend className="mb-2 inline-flex items-center gap-2 text-sm font-semibold"><UserRound className="h-4 w-4" />{t("dm_persons_title")}</legend>
                <div className="flex flex-wrap gap-2">{[1, 2, 3, 4, 5, 6].map((n) => <Choice key={n} testId={"demo-persons-" + n} active={persons === n} onClick={() => { answer("persons"); setPersons(n); }}>{n}</Choice>)}</div>
                <p className="mt-2 text-xs text-muted-foreground">{t("dm_persons_hint")}</p>
              </fieldset>
              {compatible.filter((r) => r.tier === "free").length === 0 && <p role="alert" className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{t("demo_no_compatible_recipes")}</p>}
              <p className="rounded-xl bg-secondary/70 p-3 text-xs leading-relaxed text-muted-foreground">{t("demo_data_local")}</p>
              <Button type="button" onClick={() => setTab("plan")} className="rounded-full px-6" data-testid="demo-compose">{t("dm_compose")} <Sparkles className="ml-2 h-4 w-4" /></Button>
            </div>
          </section>
        )}

        {tab === "recipes" && (
          <section className="mt-5" aria-label={t("dm_tab_recipes")}>
            <div className="mb-4 flex items-start gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-chenia-sage/30 text-chenia-espresso"><Utensils className="h-5 w-5" /></span>
              <div><h2 className="font-serif text-2xl font-semibold">{t("demo_free_recipes_title")}</h2><p className="mt-1 text-sm text-muted-foreground">{t("dm_free_only_note")}</p></div>
            </div>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Choice active={!cat} onClick={() => setCat("")} testId="demo-cat-all">{t("dm_all_cats")}</Choice>
              {CATS.map((c) => <Choice key={c} active={cat === c} onClick={() => setCat(c)} testId={`demo-cat-${c}`}>{t(`demo_${c}`)}</Choice>)}
              <span className="ml-auto text-xs text-muted-foreground" data-testid="demo-recipes-count">{t("dm_showing", { count: shownRecipes.length })}</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3" data-testid="demo-recipe-grid">
              {shownRecipes.map((r) => <DemoRecipeCard key={r.id} recipe={r} onView={setViewing} />)}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">{t("dm_cost_note", { converted: recipePayload?.currency === "eur" ? t("dm_cost_converted") : "" })}</p>
          </section>
        )}

        {tab === "plan" && <DemoMenu plan={plan} days={days} slots={slots} persons={persons} recipesById={compatibleById} onPick={setPicking} onRemove={remove} onView={setViewing} onReset={() => setPlan({})} />}
        {tab === "grocery" && <DemoGrocery grocery={grocery} persons={persons} onGoMenu={() => setTab("plan")} />}

        <DemoPlans pricing={pricing} pricingLoading={pricingLoading} pricingError={pricingError} billingCountry={billingCountry} onCountry={setBillingCountry} countries={pricing?.countries || BILLING_COUNTRIES} />

        <section className="mt-5 rounded-3xl border border-chenia-sage/40 bg-chenia-sage/15 p-6 shadow-sm sm:p-8">
          <h2 className="font-serif text-2xl font-semibold">{t("demo_save_household_title")}</h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">{t("demo_save_household_text")}</p>
          <Link to={user ? "/household" : "/register"}><Button size="lg" className="mt-5 rounded-full">{t(user ? "nav_household" : "demo_create_account_save")}</Button></Link>
        </section>
      </section>

      <DemoRecipeDialog recipe={viewing} onClose={() => setViewing(null)} />
      <DemoSlotPicker target={picking} recipes={compatible} current={picking ? plan[picking.day]?.[picking.slot] : null} onPick={pick} onView={setViewing} onClose={() => setPicking(null)} />
    </main>
  );
}
